import type { ChangeEvent, FC, FormEvent, ReactElement } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useUITranslation } from '../../../services/uiTranslations/reactBindings/hooks';
import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { Icon } from '../../icons/icon';
import { Icons } from '../../icons/iconNames';
import { InputButton } from '../../inputButton/InputButton';
import { InputGroup } from '../../inputGroup/InputGroup';
import { BaseInput } from '../../inputs/baseInput/BaseInput';
import { MiniChatInput } from '../miniChatInput/MiniChatInput';

import { SupportingText } from './SupportingText';

import styles from './MessageInput.module.css';

interface MessageInputProps {
  isBetRequiredToChat: boolean;
  isGameChat: boolean;
  isGameChatUnavailable: boolean;
  placeholder: string;
  disabled: boolean;
  onSubmit: (input: string) => void;
  cashierCallback?: () => void;
  onInputFocus?: (inputFocused: boolean) => void;
  characterLimit?: number;
  isMiniChat?: boolean;
}

export const MessageInput: FC<MessageInputProps> = ({
  cashierCallback,
  characterLimit = 200,
  isBetRequiredToChat,
  isGameChat,
  isGameChatUnavailable,
  isMiniChat,
  disabled,
  placeholder,
  onSubmit,
  onInputFocus,
}): ReactElement => {
  const [gameChatInputValue, setGameChatInputValue] = useState(''); // todo: consider moving input value to redux
  const [supportChatInputValue, setSupportChatInputValue] = useState(''); // todo: consider moving input value to redux

  const [inputFocused, setInputFocused] = useState(false);

  const chatInputRef = useRef<HTMLInputElement>(null);
  const triggeredMouseEvent = useRef<boolean>(false);

  const translatedPlaceholder = useUITranslation(placeholder);

  const inputValue = isGameChat ? gameChatInputValue : supportChatInputValue;
  const setInputValue = isGameChat ? setGameChatInputValue : setSupportChatInputValue;

  const isOverLimit = useMemo(() => inputValue.length === characterLimit, [inputValue, characterLimit]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.slice(0, characterLimit);
      setInputValue(value);
    },
    [characterLimit, setInputValue]
  );

  // Handling chatunaviable messages when click on disabled button
  const handleMouseDownEvent = useCallback(() => {
    if (isGameChatUnavailable && !triggeredMouseEvent.current) {
      triggeredMouseEvent.current = true;
    }
  }, [isGameChatUnavailable]);

  const handleInputFocus = useCallback(
    (inputFocused: boolean) => {
      if (isGameChatUnavailable) {
        if (triggeredMouseEvent.current && !inputFocused) {
          setInputFocused(true);
        } else {
          setInputFocused(inputFocused);
        }
        triggeredMouseEvent.current = false;
      }
      if (onInputFocus) {
        onInputFocus(inputFocused);
      }
    },
    [isGameChatUnavailable, setInputFocused, onInputFocus]
  );

  const handleSendMessage = useCallback(() => {
    // ignoring send message in  bet requirement and insufficient funds messages cases
    if (isGameChatUnavailable) {
      chatInputRef.current?.focus();
      setInputFocused(true);
      return;
    }
    // Prevent from sending an empty message
    if (inputValue.trim()) {
      onSubmit(inputValue);
      setInputValue('');
    }
  }, [inputValue, isGameChatUnavailable, onSubmit, setInputValue]);

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      handleSendMessage();
    },
    [handleSendMessage]
  );

  const shouldShowSupportMessage = useMemo(
    () => !disabled && isGameChatUnavailable && inputFocused,
    [disabled, isGameChatUnavailable, inputFocused]
  );

  const chatUnavaiableOptions = useMemo(() => {
    if (isGameChatUnavailable) {
      return {
        fill: `var(--core-color-button-disable-base)`,
      };
    }
    return {};
  }, [isGameChatUnavailable]);

  const mods = useModifiers(
    styles,
    'messageInput',
    {
      miniChat: isMiniChat,
      disabled: disabled,
    },
    { withBaseClass: true }
  );

  return (
    <form className={mods} onSubmit={handleFormSubmit} id="chat-form" autoComplete="off">
      {isMiniChat ? (
        <MiniChatInput
          inputRef={chatInputRef}
          disabled={disabled}
          errorMessage={isOverLimit && !isGameChatUnavailable ? <L10nText tk="CHAT_MAX" /> : null}
          isGameChatUnavailable={isGameChatUnavailable}
          maxLength={characterLimit}
          name="chat-message-input"
          onBlur={() => handleInputFocus(false)}
          onChange={handleChange}
          onFocus={() => handleInputFocus(true)}
          placeholder={translatedPlaceholder}
          handleMouseDownEvent={handleMouseDownEvent}
          supportingText={
            shouldShowSupportMessage ? (
              <SupportingText isBetRequiredToChat={isBetRequiredToChat} cashierCallback={cashierCallback} />
            ) : null
          }
          value={inputValue}
        />
      ) : (
        <InputGroup
          input={
            <BaseInput
              inputRef={chatInputRef}
              disabled={disabled}
              errorMessage={isOverLimit && !isGameChatUnavailable ? <L10nText tk="CHAT_MAX" /> : null}
              maxLength={characterLimit}
              name="chat-message-input"
              onBlur={() => handleInputFocus(false)}
              onChange={handleChange}
              onFocus={() => handleInputFocus(true)}
              placeholder={translatedPlaceholder}
              supportingText={
                shouldShowSupportMessage ? (
                  <SupportingText isBetRequiredToChat={isBetRequiredToChat} cashierCallback={cashierCallback} />
                ) : null
              }
              value={inputValue}
              containerClass={styles.messageInput__inputContainer}
            />
          }
        >
          {disabled ? null : (
            <InputButton
              disabled={isGameChatUnavailable ? false : !inputValue?.trim()}
              icon={<Icon {...chatUnavaiableOptions} icon={Icons.Send} />}
              onClick={handleSendMessage}
              onMouseDown={handleMouseDownEvent}
            />
          )}
        </InputGroup>
      )}
    </form>
  );
};
